import { useState } from "react";
import {
  Button,
  Drawer,
  DrawerSection,
  FailedCallout,
  ReasonField,
  TextInput,
  useAppNavigate,
  useReason,
  useToast,
  useCommandMutation,
} from "@amber/ui";
import { NAME_TYPE_LABELS, type ProfileDetail } from "../../contracts/index";
import { useProfileInvalidation } from "../invalidation";
import { profileCommands } from "../queries";

export function SplitPanel({ profile, onClose }: { profile: ProfileDetail; onClose: () => void }) {
  const toast = useToast();
  const invalidation = useProfileInvalidation();
  const navigate = useAppNavigate();
  const [name, setName] = useState("");
  const [names, setNames] = useState<string[]>([]);
  const reason = useReason();
  const mutation = useCommandMutation({
    run: (csrf, text: string) =>
      profileCommands.split(
        profile.id,
        { base_revision: profile.revision, name: name.trim(), names, source_ids: [], reason: text },
        csrf,
      ),
    ...invalidation,
    onSuccess: (r) => {
      toast.show(
        r.state === "started"
          ? `Draft created: ${name.trim()}; attributions move when editing of an affected transcript ends`
          : `Draft created: ${name.trim()}`,
      );
      onClose();
      navigate(`/profiles/${r.profile_id}`);
    },
  });
  return (
    <Drawer
      title="Split into a new profile"
      subtitle={profile.name}
      onClose={onClose}
      footer={
        <span className="flex items-center justify-end gap-2">
          <span className="mr-auto text-md text-muted">Nothing changes until you confirm.</span>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            type="submit"
            form="split-form"
            aria-busy={mutation.isPending || undefined}
          >
            Split
          </Button>
        </span>
      }
    >
      <form
        id="split-form"
        noValidate
        className="flex flex-col gap-4 text-ui"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.submit() && name.trim()) mutation.mutate(reason.trimmed);
        }}
      >
        <DrawerSection title="New profile">
          <TextInput
            aria-label="Name of the new profile"
            placeholder="Name of the other person"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          {reason.submitted && !name.trim() ? (
            <p className="m-0 mt-1 text-md text-attention">Enter the other person&apos;s name</p>
          ) : null}
        </DrawerSection>
        <DrawerSection title="Names that belong to the other person">
          {profile.names.length === 0 ? <p className="m-0 text-md text-muted">No other names recorded.</p> : null}
          {profile.names.map((n) => (
            <label key={n.value} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={names.includes(n.value)}
                onChange={(e) => {
                  setNames(e.target.checked ? [...names, n.value] : names.filter((x) => x !== n.value));
                }}
              />
              {n.value} <span className="text-muted">· {NAME_TYPE_LABELS[n.type]}</span>
            </label>
          ))}
        </DrawerSection>
        <p className="m-0 text-md text-muted">
          The new profile is a Draft and isn&apos;t monitored until sources are approved and a plan is saved. Both
          profiles record the split. A source in the monitoring plan must be removed from the plan before it can move.
        </p>
        <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
        <FailedCallout error={mutation.error} className="" />
      </form>
    </Drawer>
  );
}
